/**
 * Performance Configuration
 * Central configuration for all performance optimizations
 */

export const performanceConfig = {
  // Caching configuration
  cache: {
    enabled: true,
    defaultTTL: 5 * 60 * 1000, // 5 minutes
    maxSize: 50 * 1024 * 1024, // 50MB
    strategies: {
      accounts: {
        ttl: 10 * 60 * 1000,
        storage: 'memory' as const
      },
      reports: {
        ttl: 30 * 60 * 1000,
        storage: 'session' as const
      },
      staticData: {
        ttl: 60 * 60 * 1000,
        storage: 'local' as const
      }
    }
  },

  // Virtual scrolling configuration
  virtualScrolling: {
    enabled: true,
    defaultItemHeight: 60,
    buffer: 3,
    thresholds: {
      invoices: 50,
      accounts: 100,
      transactions: 100,
      users: 50
    }
  },

  // Lazy loading configuration
  lazyLoading: {
    enabled: true,
    threshold: 0.1,
    rootMargin: '50px',
    defaultDelay: 0,
    preloadRoutes: [
      'Dashboard',
      'ChartOfAccounts',
      'Invoices'
    ]
  },

  // Bundle optimization
  bundleOptimization: {
    enabled: true,
    chunkSizeLimit: 500 * 1024, // 500KB
    vendors: {
      // Split large vendors into separate chunks
      vue: ['vue', 'vue-router', 'pinia'],
      ui: ['@tanstack/vue-table', '@vueuse/core'],
      utils: ['lodash-es', 'decimal.js', 'date-fns'],
      charts: ['chart.js', 'vue-chartjs']
    }
  },

  // Image optimization
  imageOptimization: {
    enabled: true,
    lazy: true,
    webp: true,
    sizes: {
      thumbnail: { width: 150, height: 150 },
      small: { width: 300, height: 300 },
      medium: { width: 600, height: 600 },
      large: { width: 1200, height: 1200 }
    },
    quality: {
      webp: 85,
      jpeg: 80,
      png: 90
    }
  },

  // Performance monitoring
  monitoring: {
    enabled: true,
    sampleRate: 1.0, // 100% sampling in development
    thresholds: {
      pageLoad: 3000,
      apiCall: 1000,
      componentRender: 100,
      bundleSize: 500 * 1024
    },
    reportInterval: 60000, // 1 minute
    enabledMetrics: [
      'navigation',
      'resource',
      'paint',
      'layout-shift',
      'first-input-delay'
    ]
  },

  // Prefetching configuration
  prefetching: {
    enabled: true,
    routes: true,
    links: true,
    dns: true,
    preconnect: [
      'https://api.numera.com',
      'https://cdn.numera.com'
    ]
  },

  // Service worker configuration
  serviceWorker: {
    enabled: true,
    skipWaiting: true,
    clientsClaim: true,
    cacheStrategies: {
      // API calls - Network first, fall back to cache
      api: 'network-first',
      // Static assets - Cache first
      assets: 'cache-first',
      // Images - Cache first with network update
      images: 'stale-while-revalidate'
    },
    offlinePages: [
      '/offline',
      '/login'
    ],
    excludePatterns: [
      /^\/api\/auth/,
      /^\/api\/realtime/
    ]
  },

  // Memory management
  memoryManagement: {
    enabled: true,
    gcThreshold: 100 * 1024 * 1024, // 100MB
    cleanupInterval: 5 * 60 * 1000, // 5 minutes
    maxStoreSize: 10000, // Maximum items in stores
    pruneOldData: true
  },

  // Request optimization
  requestOptimization: {
    enabled: true,
    batchRequests: true,
    batchDelay: 50, // 50ms
    maxBatchSize: 10,
    compression: true,
    retryConfig: {
      maxRetries: 3,
      retryDelay: 1000,
      retryMultiplier: 2
    }
  },

  // Rendering optimization
  renderingOptimization: {
    enabled: true,
    deferredComponents: [
      'Charts',
      'Reports',
      'DataTables'
    ],
    useWebWorkers: true,
    useRequestIdleCallback: true,
    debounceDelay: 300,
    throttleDelay: 100
  }
}

// Environment-specific overrides
if (import.meta.env.PROD) {
  // Production optimizations
  performanceConfig.monitoring.sampleRate = 0.1 // 10% sampling
  performanceConfig.cache.enabled = true
  performanceConfig.serviceWorker.enabled = false
} else {
  // Development settings
  performanceConfig.monitoring.sampleRate = 1.0 // 100% sampling
  performanceConfig.serviceWorker.enabled = false // Disable SW in dev
}

// Feature flags for gradual rollout
export const performanceFeatures = {
  useVirtualScrolling: (listSize: number, threshold: number = 100) => {
    return performanceConfig.virtualScrolling.enabled && listSize > threshold
  },

  useLazyLoading: (routeName?: string) => {
    if (!performanceConfig.lazyLoading.enabled) return false
    if (!routeName) return true
    return !performanceConfig.lazyLoading.preloadRoutes.includes(routeName)
  },

  useCache: (endpoint?: string) => {
    return performanceConfig.cache.enabled
  },

  useServiceWorker: () => {
    return performanceConfig.serviceWorker.enabled && 'serviceWorker' in navigator
  }
}

// Performance utilities
export const performanceUtils = {
  /**
   * Debounce function for performance
   */
  debounce: <T extends (...args: any[]) => any>(
    fn: T,
    delay: number = performanceConfig.renderingOptimization.debounceDelay
  ): ((...args: Parameters<T>) => void) => {
    let timeoutId: ReturnType<typeof setTimeout>
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => fn(...args), delay)
    }
  },

  /**
   * Throttle function for performance
   */
  throttle: <T extends (...args: any[]) => any>(
    fn: T,
    delay: number = performanceConfig.renderingOptimization.throttleDelay
  ): ((...args: Parameters<T>) => void) => {
    let lastCall = 0
    return (...args: Parameters<T>) => {
      const now = Date.now()
      if (now - lastCall >= delay) {
        lastCall = now
        fn(...args)
      }
    }
  },

  /**
   * Request idle callback wrapper
   */
  requestIdleCallback: (
    callback: () => void,
    options?: { timeout?: number }
  ): void => {
    if ('requestIdleCallback' in window && performanceConfig.renderingOptimization.useRequestIdleCallback) {
      (window as any).requestIdleCallback(callback, options)
    } else {
      setTimeout(callback, 0)
    }
  },

  /**
   * Batch requests utility
   */
  batchRequests: <T>(
    requests: Array<() => Promise<T>>,
    batchSize: number = performanceConfig.requestOptimization.maxBatchSize
  ): Promise<T[]> => {
    const batches: Array<Array<() => Promise<T>>> = []

    for (let i = 0; i < requests.length; i += batchSize) {
      batches.push(requests.slice(i, i + batchSize))
    }

    return batches.reduce(async (promise, batch) => {
      const results = await promise
      const batchResults = await Promise.all(batch.map(fn => fn()))
      return [...results, ...batchResults]
    }, Promise.resolve([] as T[]))
  }
}

export default performanceConfig
